import { apiSlice } from './apiSlice';
import { STATISTICS_URL } from '../constants';

export const statisticsApiSlice = apiSlice.injectEndpoints({
	endpoints: (builder) => ({
		getOrderPrimaryPhaseStats: builder.query({
			query: (params) => ({
				url: `${STATISTICS_URL}/order/primary-phase`,
				params,
			}),
			keepUnusedDataFor: 120,
		}),
		postOrderPrimaryPhaseStatsBatch: builder.mutation({
			query: (items) => ({
				url: `${STATISTICS_URL}/order/primary-phase/batch`,
				method: 'POST',
				body: { items },
			}),
		}),
		getStatisticsOrderYearRange: builder.query({
			query: () => ({
				url: `${STATISTICS_URL}/order/year-range`,
			}),
			keepUnusedDataFor: 3600,
		}),
		/** 按统计筛选导出订单汇总（列口径同 npm run exports:orders-summary） */
		exportStatisticsOrdersSummary: builder.mutation({
			query: (params) => ({
				url: `${STATISTICS_URL}/order/orders-summary-export`,
				params,
				responseHandler: async (response) => {
					if (!response.ok) {
						const err = await response.json().catch(() => ({ message: '导出失败' }));
						throw Object.assign(new Error(err.message || '导出失败'), { status: response.status, data: err });
					}
					const blob = await response.blob();
					const disposition = response.headers.get('Content-Disposition') || '';
					const matched = disposition.match(/filename="?([^"]+)"?/i);
					return { blob, fileName: matched?.[1] || `orders-summary-${Date.now()}.xlsx` };
				},
				cache: 'no-cache',
			}),
		}),
		getStatisticsDashboardCharts: builder.query({
			query: () => ({
				url: `${STATISTICS_URL}/dashboard/charts`,
			}),
			providesTags: ['StatisticsDashboardCharts'],
			keepUnusedDataFor: 300,
		}),
		putStatisticsDashboardCharts: builder.mutation({
			query: (charts) => ({
				url: `${STATISTICS_URL}/dashboard/charts`,
				method: 'PUT',
				body: { charts },
			}),
			async onQueryStarted(charts, { dispatch, queryFulfilled }) {
				const patchResult = dispatch(
					statisticsApiSlice.util.updateQueryData('getStatisticsDashboardCharts', undefined, (draft) => {
						draft.charts = charts;
					}),
				);
				try {
					const { data } = await queryFulfilled;
					dispatch(
						statisticsApiSlice.util.updateQueryData('getStatisticsDashboardCharts', undefined, (draft) => {
							draft.charts = data?.charts ?? charts;
						}),
					);
				} catch {
					patchResult.undo();
				}
			},
		}),
		postRestoreStatisticsDashboardDefaultCharts: builder.mutation({
			query: () => ({
				url: `${STATISTICS_URL}/dashboard/charts/restore-defaults`,
				method: 'POST',
			}),
			async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
				try {
					const { data } = await queryFulfilled;
					dispatch(
						statisticsApiSlice.util.updateQueryData('getStatisticsDashboardCharts', undefined, (draft) => {
							draft.charts = data?.charts ?? [];
						}),
					);
				} catch {
					/* 失败时保留本地/缓存现状 */
				}
			},
		}),
		getHomeDashboardCharts: builder.query({
			query: () => ({
				url: `${STATISTICS_URL}/home/charts`,
			}),
			providesTags: ['HomeDashboardCharts'],
			keepUnusedDataFor: 300,
		}),
		putHomeDashboardCharts: builder.mutation({
			query: (charts) => ({
				url: `${STATISTICS_URL}/home/charts`,
				method: 'PUT',
				body: { charts },
			}),
			async onQueryStarted(charts, { dispatch, queryFulfilled }) {
				const patchResult = dispatch(
					statisticsApiSlice.util.updateQueryData('getHomeDashboardCharts', undefined, (draft) => {
						draft.charts = charts;
					}),
				);
				try {
					const { data } = await queryFulfilled;
					dispatch(
						statisticsApiSlice.util.updateQueryData('getHomeDashboardCharts', undefined, (draft) => {
							draft.charts = data?.charts ?? charts;
						}),
					);
				} catch {
					patchResult.undo();
				}
			},
		}),
	}),
});

export const {
	useGetOrderPrimaryPhaseStatsQuery,
	useGetStatisticsOrderYearRangeQuery,
	useExportStatisticsOrdersSummaryMutation,
	usePostOrderPrimaryPhaseStatsBatchMutation,
	useGetStatisticsDashboardChartsQuery,
	usePutStatisticsDashboardChartsMutation,
	usePostRestoreStatisticsDashboardDefaultChartsMutation,
	useGetHomeDashboardChartsQuery,
	usePutHomeDashboardChartsMutation,
} = statisticsApiSlice;
